// src/utils/menu/menuModel.js
// Turns on-shelf products and pricing tiers into the content of the flower menu,
// and lists anything that must be fixed before the menu can be generated.
import { MENU_COLORS, DEMS_TIERS, POTENCY_TIERS, aromaForColor, demsTier, menuColor, potencyTier } from "./menuMetrics";

// What the row above the menu header shows. LOGO and BOTH need the license to have a logo.
export const BRAND_STYLES = { LOGO: "logo", NAME: "name", BOTH: "both" };

// Every price line on the menu, in print order. All five are required per tier.
export const PRICE_LINES = [
    { key: "price_per_gram", label: "G:" },
    { key: "price_per_eighth", label: "1/8:" },
    { key: "price_per_quarter", label: "1/4:" },
    { key: "price_per_half", label: "1/2:" },
    { key: "price_per_ounce", label: "OZ:" },
];

const priceValue = (v) => {
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
};

export function formatMenuPrice(v) {
    const n = priceValue(v);
    if (n == null) return "";
    return `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
}

/**
 * "Wednesday, May 20, 2026" from a date input value.
 *
 * @param {string} isoDate - "YYYY-MM-DD"
 */
export function formatMenuDate(isoDate) {
    const [y, m, d] = (isoDate || "").split("-").map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    if (!y || isNaN(date)) return "";
    return date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

// The code printed in a card's DEMS cell: DEMS-L, DEMS-M or DEMS-H.
export function demsLabel(tier) {
    return DEMS_TIERS[tier]?.code || "";
}

// Lowest gram price first, then lowest ounce price, then name. Blank prices sort last.
function compareTiers(a, b) {
    for (const key of ["price_per_gram", "price_per_ounce"]) {
        const av = priceValue(a[key]) ?? Infinity;
        const bv = priceValue(b[key]) ?? Infinity;
        if (av !== bv) return av - bv;
    }
    return (a.name || "").localeCompare(b.name || "");
}

function compareCards(a, b) {
    const byColor = MENU_COLORS.indexOf(a.color) - MENU_COLORS.indexOf(b.color);
    return byColor !== 0 ? byColor : a.name.localeCompare(b.name);
}

const nameList = (products) => products.map((p) => p.product_name).join(", ");

/**
 * @param {object}   args
 * @param {Array}    args.products     - On-shelf menu products, each with its chosen `price_tier_id`.
 * @param {Array}    args.tiers        - Pricing tiers from GET /pricing-tiers.
 * @param {string}   args.date         - "YYYY-MM-DD".
 * @param {string}   args.companyName
 * @param {string}  [args.locationName]
 * @param {string}  [args.brandStyle]  - One of BRAND_STYLES; defaults to NAME.
 * @returns {{ header: object, brandStyle: string, sections: Array, blockers: Array<{key: string, message: string}> }}
 */
export function buildMenuModel({ products, tiers, date, companyName, locationName, brandStyle = BRAND_STYLES.NAME }) {
    const tierById = new Map((tiers || []).map((t) => [String(t.id), t]));
    const blockers = [];

    const untiered = [];
    const missingLabData = [];
    const cardsByTier = new Map();

    for (const product of products || []) {
        const tier = tierById.get(String(product.price_tier_id ?? ""));
        const potency = potencyTier(product);
        const dems = demsTier(product);
        if (!tier) untiered.push(product);
        if (!potency || !dems) missingLabData.push(product);
        if (!tier || !potency || !dems) continue;

        const color = menuColor(product);
        const cards = cardsByTier.get(tier.id) || [];
        cards.push({
            productId: product.product_id,
            name: product.product_name || "",
            color,
            aroma: aromaForColor(color) || "",
            potencyBars: POTENCY_TIERS[potency].bars,
            demsLabel: demsLabel(dems),
        });
        cardsByTier.set(tier.id, cards);
    }

    if (untiered.length > 0) {
        blockers.push({ key: "no-tier", message: `No price tier: ${nameList(untiered)}` });
    }
    if (missingLabData.length > 0) {
        blockers.push({
            key: "no-lab-data",
            message: `Lab results are missing the cannabinoid or terpene values needed for potency and DEMS: ${nameList(missingLabData)}`,
        });
    }

    // Every tier a product is assigned to must be fully priced, including products held back above.
    const usedTierIds = new Set((products || []).map((p) => String(p.price_tier_id ?? "")));
    const usedTiers = [...tierById.values()].filter((t) => usedTierIds.has(String(t.id))).sort(compareTiers);
    for (const tier of usedTiers) {
        const missing = PRICE_LINES.filter((line) => priceValue(tier[line.key]) == null).map((line) => line.label.replace(":", ""));
        if (missing.length > 0) {
            blockers.push({
                key: `tier-prices-${tier.id}`,
                message: `Price tier "${tier.name}" has no ${missing.join(", ")} price. Set it in Settings > Pricing.`,
            });
        }
    }

    const sections = usedTiers
        .filter((tier) => cardsByTier.has(tier.id))
        .map((tier) => ({
            tierId: tier.id,
            title: tier.name,
            prices: PRICE_LINES.map((line) => ({ label: line.label, text: formatMenuPrice(tier[line.key]) })),
            cards: cardsByTier.get(tier.id).sort(compareCards),
        }));

    return {
        header: {
            storeLine: [companyName, locationName].filter(Boolean).join(" — "),
            title: "Flower Menu",
            dateText: formatMenuDate(date),
        },
        brandStyle,
        sections,
        blockers,
    };
}
